import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  DokaTable,
  DokaThead,
  DokaTh,
  DokaTr,
  DokaTbody,
  DokaTd,
  DokaDetailsTable,
  DokaDetailsTr,
  DokaDetailsTd,
  DokaDetailsThead,
  DokaDetailsTh,
  DokaDetailsTbody,
  DokaActionMenu,
  DokaActionMenuItem,
  DokaSearchField,
  DokaIcon,
  DokaModalController,
  DokaButtonGroup,
  DokaButtonGroupButton,
  DokaSelect,
  DokaSelectOption,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import {
  CatalogClient,
  CatalogItemModel,
  ICatalogCategoryModel,
  LocationsClient,
} from 'src/api/services/generated/api-clients';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { CreateCatalogItemSlideOverComponent } from '../slide-overs/create-catalog-item-slide-over/create-catalog-item-slide-over.component';
import { CreateCatalogCategorySlideOverComponent } from '../slide-overs/create-catalog-category-slide-over/create-catalog-category-slide-over.component';
import { EditCatalogCategorySlideOverComponent } from '../slide-overs/edit-catalog-category-slide-over/edit-catalog-category-slide-over.component';
import { EditCatalogItemSlideOverComponent } from '../slide-overs/edit-catalog-item-slide-over/edit-catalog-item-slide-over.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { firstValueFrom } from 'rxjs';
import { UserInfoService } from 'src/app/shared/services/user-info.service';

@Component({
  selector: 'app-master-data-catalog-grid',
  templateUrl: './catalog-grid.component.html',
  imports: [
    DokaTable,
    DokaThead,
    DokaTh,
    DokaTr,
    DokaTbody,
    DokaTd,
    DokaDetailsTable,
    DokaDetailsTr,
    DokaDetailsTd,
    DokaDetailsThead,
    DokaDetailsTh,
    DokaDetailsTbody,
    DokaActionMenu,
    DokaActionMenuItem,
    I18NextEagerPipe,
    DokaSearchField,
    SpinnerComponent,
    DokaIcon,
    DokaButtonGroup,
    DokaButtonGroupButton,
    DokaSelect,
    DokaSelectOption,
  ],
})
export class CatalogGridComponent {
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);
  private readonly modalService = inject(ModalService);
  private readonly catalogClient = inject(CatalogClient);
  private readonly locationsClient = inject(LocationsClient);
  private readonly router = inject(Router);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly userInfoService = inject(UserInfoService);

  protected searchText = signal('');
  protected readonly selectedDivisionId = signal<string | undefined>(undefined);

  protected readonly divisions = rxResource({
    stream: () => this.locationsClient.getDivisions(),
  });

  protected readonly effectiveDivisionId = computed(() => {
    return this.selectedDivisionId() ?? this.userInfoService.defaultDivisionId(this.divisions.value()?.data ?? []);
  });

  protected readonly catalogCategories = rxResource({
    params: () => ({ divisionId: this.effectiveDivisionId() }),
    stream: ({ params }) => this.catalogClient.getCatalogCategories(params.divisionId),
  });

  protected readonly catalogItems = rxResource({
    params: () => ({ divisionId: this.effectiveDivisionId() }),
    stream: ({ params }) => this.catalogClient.getCatalogItems(params.divisionId),
  });

  protected readonly detailedCatalogCategories = computed(() => {
    const categories = this.catalogCategories.value()?.data ?? [];
    const items = this.catalogItems.value()?.data ?? [];

    return categories
      .map((category) => ({
        ...category,
        items: items.filter((item) => item.catalogCategoryId === category.id),
        isExpanded: false,
      }))
      .filter(
        (category) =>
          category.name.toLowerCase().includes(this.searchText().toLowerCase()) ||
          category.code.toLowerCase().includes(this.searchText().toLowerCase()) ||
          category.items.some((item: CatalogItemModel) =>
            item.name.toLowerCase().includes(this.searchText().toLowerCase()),
          ),
      );
  });

  protected navigateToTemplateDetails(catalogItemId: string) {
    this.router.navigate(['master-data', 'catalog', catalogItemId]);
  }

  protected async createNewCatalogItem() {
    const modal = await this.modalCtrl.create(
      {
        component: CreateCatalogItemSlideOverComponent,
        componentProps: {
          divisionId: this.effectiveDivisionId,
        },
      },
      'slide-over',
    );

    await modal.present();

    await this.onModalDismiss(modal);
  }

  protected async createNewCatalogCategory() {
    const modal = await this.modalCtrl.create(
      {
        component: CreateCatalogCategorySlideOverComponent,
        componentProps: {
          divisionId: this.effectiveDivisionId,
        },
      },
      'slide-over',
    );

    await modal.present();

    await this.onModalDismiss(modal);
  }

  protected async confirmDeleteCatalogItem(catalogItem: CatalogItemModel) {
    const decision = await this.modalService.openDeleteModal(
      this.i18n.t('DeleteCatalogItem.Content', { itemName: catalogItem.name }),
      this.i18n.t('DeleteCatalogItem.Title'),
    );

    if (decision) {
      try {
        await firstValueFrom(this.catalogClient.deleteCatalogItem(catalogItem.id));
        await this.toastService.showSuccess(
          this.i18n.t('Common.SuccessDeleteToast', { itemName: `Catalog Item ${catalogItem.name}` }),
        );
        this.catalogItems.reload();
      } catch {
        await this.toastService.showError(
          this.i18n.t('Common.ErrorDeleteToast', { itemName: `Catalog Item ${catalogItem.name}` }),
        );
      }
    }
  }

  protected async editCatalogCategory(category: ICatalogCategoryModel) {
    const modal = await this.modalCtrl.create(
      {
        component: EditCatalogCategorySlideOverComponent,
        componentProps: {
          category: signal(category).asReadonly(),
        },
      },
      'slide-over',
    );

    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (data && role === 'updated') {
      this.catalogCategories.reload();
      this.catalogItems.reload();
    }
  }

  protected async editCatalogItem(catalogItem: CatalogItemModel) {
    const modal = await this.modalCtrl.create(
      {
        component: EditCatalogItemSlideOverComponent,
        componentProps: {
          catalogItem: signal(catalogItem).asReadonly(),
        },
      },
      'slide-over',
    );

    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (data && role === 'updated') {
      this.catalogCategories.reload();
      this.catalogItems.reload();
    }
  }

  protected onDivisionChange(divisionId: string) {
    this.selectedDivisionId.set(divisionId);
    this.userInfoService.rememberDivision(divisionId);
  }

  private async onModalDismiss(modal: HTMLIonModalElement) {
    const { data, role } = await modal.onDidDismiss();
    if (data && role === 'created') {
      this.catalogCategories.reload();
      this.catalogItems.reload();
    }
  }
}
